import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getActivePlans } from '../services/pricingPlanService';
import type { PricingPlan } from '../services/pricingPlanService';
import { createCheckoutSession } from '../services/stripeService';

/**
 * Trial Expired Page
 *
 * Shown when a company's trial has expired. Displays the billing breakdown
 * based on current user roles and a Subscribe button that redirects to
 * Stripe Checkout.
 */

const ROLE_LABELS: Record<string, string> = {
  company_admin: 'Company Admin',
  project_manager: 'Project Manager',
  project_manager_lite: 'PM Lite',
  contributor: 'Contributor',
  taskboard_user: 'Taskboard User',
  stakeholder: 'Stakeholder',
};

export function TrialExpired(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('company_id') || '';
  const companyName = searchParams.get('company_name') || '';
  const email = searchParams.get('email') || '';

  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getActivePlans()
      .then(data => setPlans(data.filter(p => p.role_key && (p.price_monthly || 0) > 0)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async () => {
    if (!companyId || !email) {
      setError('Missing account information. Please log in again.');
      return;
    }
    setSubscribing(true);
    setError('');
    try {
      const result = await createCheckoutSession(companyId, email);
      window.location.href = result.checkout_url;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start checkout. Please try again or contact support.');
      setSubscribing(false);
    }
  };

  // Find company admin plan to show the minimum they'll pay
  const adminPlan = plans.find(p => p.role_key === 'company_admin');
  const minMonthly = adminPlan?.price_monthly || 0;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo1.png" alt="Nivoon" className="h-16" />
            </Link>
            <Link
              to="/login"
              className="text-gray-700 hover:text-indigo-600 font-medium transition-colors"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Your Free Trial Has Ended
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {companyName
              ? `The trial for ${companyName} has expired. Subscribe to continue using Nivoon.`
              : 'Your trial period has expired. Subscribe to continue using Nivoon.'}
          </p>
        </div>
      </section>

      {/* Pricing & Subscribe */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* How billing works */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Per-Seat Billing</h2>
            <p className="text-gray-500 text-sm mb-6">
              You're billed monthly based on the number of users in each role. As the account owner, your Company Admin seat is included.
            </p>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <>
                {/* Role pricing table */}
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Per User / Month</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {plans.map(plan => (
                        <tr key={plan.id}>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {ROLE_LABELS[plan.role_key || ''] || plan.name}
                            {plan.role_key === 'company_admin' && (
                              <span className="ml-2 text-xs text-indigo-600 font-medium">(your role)</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                            ${plan.price_monthly}/mo
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td className="px-4 py-3 text-sm text-gray-500">Stakeholder</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-500">Free</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Starting price callout */}
                {minMonthly > 0 && (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-indigo-900">Your starting cost</p>
                        <p className="text-xs text-indigo-600 mt-0.5">1 Company Admin (you)</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-indigo-700">${minMonthly}</p>
                        <p className="text-xs text-indigo-500">/month</p>
                      </div>
                    </div>
                    <p className="text-xs text-indigo-600 mt-2">
                      Additional users are billed at their role's rate when you add them to your team.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Subscribe button */}
            {companyId && email ? (
              <button
                onClick={handleSubscribe}
                disabled={subscribing || loading}
                className="w-full py-4 px-6 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-lg shadow-lg"
              >
                {subscribing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Redirecting to checkout...
                  </span>
                ) : (
                  'Subscribe & Continue'
                )}
              </button>
            ) : (
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-3">Please log in to set up your subscription.</p>
                <Link
                  to="/login"
                  className="inline-block px-8 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all shadow-lg"
                >
                  Go to Login
                </Link>
              </div>
            )}

            <p className="text-xs text-gray-400 text-center mt-4">
              Secure payment powered by Stripe. Cancel anytime.
            </p>
          </div>

          {/* Contact section */}
          <div className="text-center">
            <p className="text-gray-500 text-sm mb-2">Have questions about billing?</p>
            <a
              href="mailto:info@nivoon.com?subject=Nivoon Billing Question"
              className="text-indigo-600 hover:text-indigo-800 font-medium text-sm transition-colors"
            >
              Contact us at info@nivoon.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="mb-4 md:mb-0">
              <img src="/logo1.png" alt="Nivoon" className="h-8 opacity-70" />
            </Link>
            <p className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} Nivoon. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TrialExpired;
